import { defineMessage, MessageDescriptor } from "react-intl"
import { ConsumerSubscription } from "#/lib/carrot-api/types/ConsumerSubscription"
import { reportErrorMessage } from "#/utils/ErrorReporting"

export const CONSUMER_REGISTRATION_RESUME_PATH = "/consumer/register/resume"

// carrot-app's `consumerSubscription` as either source carries it: users/me / the login response
// ({@link ConsumerSubscription}), or the BFF's `subscription.getStatus`, whose parser also tolerates an
// absent `url`. Every helper here takes this looser shape so both can be handed in as they are.
//
// ⚠️ users/me and the login response are NOT parsed at runtime, so the storefront's links and name are typed
// as carrot-app promises them, not as they are guaranteed to arrive. The helpers below check each value's
// runtime type and treat anything unexpected as not configured, rather than throwing mid-login.
export type ConsumerSubscriptionFacts = {
  state: ConsumerSubscription["state"]
  storefront: {
    type: ConsumerSubscription["storefront"]["type"]
    landingUrl?: string | null
    url?: string | null
    manageSubscriptionUrl?: string | null
    orderHistoryUrl?: string | null
    displayName?: string | null
  }
}

/**
 * `url` when it is a web (http or https) address, otherwise null. Store urls arrive from the server verbatim and
 * end up in an href or `window.location.assign`, both of which would run a `javascript:` url, so anything that
 * isn't a web address is treated as not configured.
 */
export function asWebUrl(url: unknown): string | null {
  return typeof url === "string" && /^https?:\/\//i.test(url) ? url : null
}

/**
 * Where a lapsed external member goes to get back in: `landingUrl`, or the legacy `url` from a carrot-app that
 * predates carrot-app#9655's rename. The ONE reader of that field. Each is checked by `asWebUrl` BEFORE falling
 * back, so an invalid `landingUrl` can't hide a valid legacy `url`; neither being a web address answers null.
 */
export function storefrontLandingUrl(storefront: ConsumerSubscriptionFacts["storefront"] | undefined): string | null {
  return asWebUrl(storefront?.landingUrl) ?? asWebUrl(storefront?.url)
}

/**
 * The store's name for copy, or null when there is none to use — carrot-app sends null for carrot and for
 * a store with no name configured, and an older carrot-app sends nothing. Blank counts as none. Always
 * passed to `formatMessage` as a plain string value, so it renders as text and never as markup.
 */
function storefrontDisplayName(storefront: ConsumerSubscriptionFacts["storefront"] | undefined): string | null {
  const name: unknown = storefront?.displayName
  const trimmed = typeof name === "string" ? name.trim() : ""
  return trimmed ? trimmed : null
}

/** A message and the values it needs, so a caller can hold it in state and format it later. */
export type StorefrontMessage = { message: MessageDescriptor; values?: { storeName: string } }

/**
 * Picks the named copy when carrot-app gives the store's name and the generic copy when it doesn't. The one
 * place that choice is made, so every store message falls back the same way.
 */
export function storefrontMessage(
  storefront: ConsumerSubscriptionFacts["storefront"] | undefined,
  { named, generic }: { named: MessageDescriptor; generic: MessageDescriptor }
): StorefrontMessage {
  const storeName = storefrontDisplayName(storefront)
  return storeName ? { message: named, values: { storeName } } : { message: generic }
}

// Only external storefronts go back to the funnel: a carrot member who is registration_incomplete has
// already paid and is waiting on the subscription sync.
export function shouldResumeExternalRegistration(subscription: ConsumerSubscriptionFacts | null | undefined): boolean {
  return subscription?.storefront.type === "external" && subscription.state === "registration_incomplete"
}

// An external-storefront member pays, cancels, reactivates and repurchases in their store, never through
// Carrot/Stripe, so none of the Stripe billing surfaces apply to them.
export function isExternalStorefrontMember(subscription: ConsumerSubscriptionFacts | null | undefined): boolean {
  return subscription?.storefront.type === "external"
}

export function isLapsedExternalStorefrontMember(subscription: ConsumerSubscriptionFacts | null | undefined): boolean {
  return isExternalStorefrontMember(subscription) && subscription?.state === "lapsed"
}

// Shown when a lapsed external member has to be sent back to their store and carrot-app gave no url for it.
// Names the store when carrot-app has a name for it; the generic wording otherwise.
const LAPSED_NO_URL_NAMED = defineMessage({
  defaultMessage: "Your Carrot membership has ended. To renew, go back to the {storeName}."
})
const LAPSED_NO_URL_GENERIC = defineMessage({
  defaultMessage: "Your Carrot membership has ended. To renew, go back to the store where you bought Carrot."
})

export function lapsedExternalMemberNoUrlMessage(
  storefront: ConsumerSubscriptionFacts["storefront"] | undefined
): StorefrontMessage {
  return storefrontMessage(storefront, { named: LAPSED_NO_URL_NAMED, generic: LAPSED_NO_URL_GENERIC })
}

/**
 * Sends a LAPSED external-storefront member to their store's landing url (`storefrontLandingUrl`), the way back in. The one copy of
 * that hand-off: login, the start-up guard, the billing pages and the funnel all call it, so no entry point
 * can disagree about where such a member lands.
 *
 * A FULL page load: the destination is off this origin. Only an http(s) url is followed.
 *
 * - `"not_applicable"` — not a lapsed external member; the caller carries on.
 * - `"sent"` — navigating away; the caller renders nothing more.
 * - `"missing_url"` — the store isn't configured (CAP-285). Reported here; the caller shows
 *   {@link lapsedExternalMemberNoUrlMessage}.
 */
export function sendLapsedExternalMemberToStorefront(
  subscription: ConsumerSubscriptionFacts | null | undefined
): "not_applicable" | "sent" | "missing_url" {
  if (!isLapsedExternalStorefrontMember(subscription)) {
    return "not_applicable"
  }
  // `storefrontLandingUrl` checks it with `asWebUrl`: a value that isn't a web address takes the missing-url
  // path, never the redirect.
  const url = storefrontLandingUrl(subscription?.storefront)
  if (url) {
    window.location.assign(url)
    return "sent"
  }
  reportErrorMessage("Lapsed external-storefront member has no storefront url")
  return "missing_url"
}
